import { Injectable, inject } from '@angular/core';
import { INavData } from '@coreui/angular-pro';
import { TranslateService } from 'i18n';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { Logger } from 'shared';
import { FeatureFlagsService } from './feature-flags.service';

export interface MenuBadgeUpdate {
    urlPattern: string;
    badge: { color: string; text: string } | undefined;
}

@Injectable({
  providedIn: 'root',
})
export abstract class MenuService<T extends INavData> {

    protected logger = new Logger('MenuService');
    protected translateService = inject(TranslateService);
    protected featureFlagsService = inject(FeatureFlagsService);
    protected navItemsSubject = new BehaviorSubject<T[]>([]);
    protected badgeSubject = new BehaviorSubject<string | null>(null);
    private isAdminUser: boolean | null = null;
    private userId: number | null = null;
    private isXshopAdminUser: boolean = false;
    private isSuperAdminUser: boolean = false;
    /** Community role keys from the bootstrap payload, for `data-visible: 'role:<key>'` items. */
    private roles: string[] = [];
    /** Per-user feature flags for `data-visible: 'flag:<name>'` items (see filterNavItemsByRole). */
    private flags: Record<string, boolean | undefined> = {};
    private allNavItems: T[] = [];
    private dynamicBadges: Map<string, { color: string; text: string } | undefined> = new Map();

    public navItems$: Observable<T[]> = this.navItemsSubject.asObservable();
    public badge$: Observable<string | null> = this.badgeSubject.asObservable();

    constructor() {
        this.translateService.langChanges
            .pipe(
                startWith(this.translateService.getActiveLang()),
                switchMap((lang) =>
                this.translateService
                    .selectTranslation(lang)
                    .pipe(
                        map(() => this.createNavItems(lang, (key: string) => this.translateService.translate(key)))
                    )
                ))
            .subscribe((navItems) => {
                this.allNavItems = navItems;
                this.applyRoleFilter();
            });
    }

    protected abstract createNavItems(lang: string, t: (key: string) => string): T[];

    protected updateNavItems(items: T[]): void {
        this.navItemsSubject.next(items);
    }

    private applyRoleFilter(): void {
        this.logger.debug('MenuService.applyRoleFilter: isAdminUser =', this.isAdminUser, 'userId =', this.userId, 'total items =', this.allNavItems.length);

        if (this.isAdminUser === null) {
            // No role set yet, don't show any items (wait for user to load)
            this.logger.debug('MenuService.applyRoleFilter: No role set, hiding all items');
            this.navItemsSubject.next([]);
            return;
        }

        const ordersEnabled = this.featureFlagsService.isOrdersEnabled();
        this.logger.debug('MenuService.applyRoleFilter: ordersEnabled =', ordersEnabled);

        const filteredItems = this.filterTree(this.allNavItems)
            .map((item) => this.applyDisabledState(item, ordersEnabled, this.isAdminUser ?? false));

        // Apply dynamic badges (e.g., unread message count)
        const itemsWithBadges = this.applyDynamicBadges(filteredItems);

        this.logger.debug('MenuService.applyRoleFilter: filtered to', itemsWithBadges.length, 'items');
        this.navItemsSubject.next(itemsWithBadges);
    }

    /**
     * Filters a nav tree by `data-visible`, recursively: a group (`children`) is
     * kept only if it is visible itself and at least one child survives — so an
     * "Administration" group vanishes for a plain admin whose entries were all
     * super-admin only, instead of rendering as an empty header.
     */
    private filterTree(items: T[]): T[] {
        return items
            .filter((item) => this.isVisible(item))
            .map((item) => {
                const children = (item as { children?: T[] }).children;
                if (!Array.isArray(children)) return item;
                return { ...item, children: this.filterTree(children) } as T;
            })
            .filter((item) => {
                const children = (item as { children?: T[] }).children;
                return !Array.isArray(children) || children.length > 0;
            });
    }

    private isVisible(item: T): boolean {
                const visibility = item.attributes?.['data-visible'];
                if (!visibility) return true;

                // Check for specific user ID (e.g., 'userId:1')
                if (visibility.startsWith('userId:')) {
                    const requiredUserId = parseInt(visibility.split(':')[1], 10);
                    const include = this.userId === requiredUserId;
                    this.logger.debug('MenuService.applyRoleFilter: item', item.name, 'visibility =', visibility, 'include =', include);
                    return include;
                }

                // Feature flag items - keep visible but will be disabled below
                if (visibility === 'orders-enabled') {
                    return true;
                }

                // Community role from the bootstrap payload (e.g. 'role:practitioner');
                // 'role:a|b' shows the item for either role. No role = the item does not
                // exist for this user (customer principle: nothing greyed out).
                if (visibility.startsWith('role:')) {
                    const wanted = visibility.slice('role:'.length).split('|');
                    const include = wanted.some((key: string) => this.roles.includes(key));
                    this.logger.debug('MenuService.applyRoleFilter: item', item.name, 'visibility =', visibility, 'include =', include);
                    return include;
                }

                // Per-user flag from the bootstrap payload (e.g. 'flag:community_enabled').
                // Only an explicit false hides the item: a backend that does not send
                // the flag leaves the menu unchanged.
                if (visibility.startsWith('flag:')) {
                    const flagName = visibility.slice('flag:'.length);
                    const include = this.flags[flagName] !== false;
                    this.logger.debug('MenuService.applyRoleFilter: item', item.name, 'visibility =', visibility, 'include =', include);
                    return include;
                }

                // Check for xshop admin
                if (visibility === 'isXshopAdmin') {
                    return this.isXshopAdminUser;
                }

                // Check for super admin (user_type 'super', or the legacy user ID 1)
                if (visibility === 'isSuperAdmin') {
                    this.logger.debug('MenuService.applyRoleFilter: item', item.name, 'visibility =', visibility, 'include =', this.isSuperAdminUser);
                    return this.isSuperAdminUser;
                }

                // Check for admin/non-admin
                const include = (visibility === 'isAdmin' && this.isAdminUser) || (visibility === 'isNotAdmin' && !this.isAdminUser);
                this.logger.debug('MenuService.applyRoleFilter: item', item.name, 'visibility =', visibility, 'include =', include);
                return include;
    }

    /**
     * Apply disabled state to menu items based on feature flags
     */
    private applyDisabledState(item: T, ordersEnabled: boolean, isAdmin: boolean): T {
        const visibility = item.attributes?.['data-visible'];

        // Check if this is an order-related item
        if (visibility === 'orders-enabled') {
            const shouldDisable = !ordersEnabled && !isAdmin;

            return {
                ...item,
                disabled: shouldDisable,
                attributes: {
                    ...item.attributes,
                    'data-disabled-reason': shouldDisable
                        ? 'This feature is temporarily unavailable in the preview version'
                        : undefined,
                    class: shouldDisable ? 'disabled-menu-item' : undefined
                }
            } as T;
        }

        return item;
    }

    public filterNavItemsByRole(
        isAdmin: boolean,
        userId?: number,
        isXshopAdmin?: boolean,
        isSuperAdmin?: boolean,
        flags?: Record<string, boolean | undefined>,
        roles?: string[]
    ): void {
        this.logger.debug('MenuService.filterNavItemsByRole: isAdmin =', isAdmin, 'userId =', userId, 'isXshopAdmin =', isXshopAdmin, 'isSuperAdmin =', isSuperAdmin, 'flags =', flags, 'roles =', roles);
        this.isAdminUser = isAdmin;
        this.userId = userId ?? null;
        this.isXshopAdminUser = isXshopAdmin ?? false;
        this.isSuperAdminUser = isSuperAdmin ?? false;
        this.flags = flags ?? {};
        this.roles = roles ?? [];
        this.applyRoleFilter();
    }

    /**
     * Update the badge for a menu item matching the URL pattern
     * @param urlPattern - Part of URL to match (e.g., '/health-concierge')
     * @param badge - Badge object or undefined to remove
     */
    public updateMenuBadge(urlPattern: string, badge: { color: string; text: string } | undefined): void {
        this.dynamicBadges.set(urlPattern, badge);
        this.applyRoleFilter(); // Re-apply to update badges
    }

    /**
     * Apply dynamic badges to nav items
     */
    private applyDynamicBadges(items: T[]): T[] {
        return items.map(item => {
            for (const [urlPattern, badge] of this.dynamicBadges.entries()) {
                if (item.url?.includes(urlPattern)) {
                    return { ...item, badge } as T;
                }
            }
            return item;
        });
    }
}